// Tour Timeline — horizontal Gantt: rows = tours, time axis = days, event
// bars + ship/drive wings along each row. A hybrid of the Calendar's bar
// visual language (accent hue per tour, dashed transit wings) and the Crew
// Schedule's sticky-left row layout. Standalone (non-tour) events render in
// faint packed lanes below the tours so tours dominate.
//
// Window = a hard 90-day span from the start (today on the default view, the
// 1st of the month once paged with ◀ ▶); horizontal scroll covers the overflow
// and items running past the right edge are clamped so their start/ship dates
// peek in. The scroll container is never unmounted on data refetch, so
// assigning vehicles keeps the user's scroll position.

import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { useApi } from "../apiHelpers";
import { useToast } from "../components/Toast/ToastContext";
import useUserStore from "../stores/useUserStore";
import { enumerateDays, formatEventDate } from "../utils/date-utils";
import VehicleAssignmentModal, { TourVehicle } from "./VehicleAssignmentModal";
import { accentForSeed } from "../Events/event-accent";
import type { EventAccent } from "../Events/event-accent";
import { buttonClass, IconButton } from "../components/ui/Button";
import Tooltip from "../components/ui/Tooltip";
import LogoSpinner from "../components/ui/LogoSpinner";
import Appear from "../components/ui/Appear";
import FadeIn from "../components/ui/FadeIn";
import { Tag } from "../components/ui/Tag";
import { IconChevronLeft, IconChevronRight } from "../components/ui/icons";

const DAY_W = 26; // px per day column
const LANE_H = 30; // px per standalone-event lane
const WINDOW_DAYS = 90; // hard visible span; horizontal scroll covers the rest

interface TimelineEvent {
	id: string;
	name: string;
	status: string;
	event_date: string;
	end_date: string | null;
	ship_out_date: string | null;
	ship_back_date: string | null;
	transport_mode: string | null;
	ls_drone_count: number;
	pyro_drone_count: number;
	tour_id: string | null;
	/** event_vehicle rows, attached by GET /events. A truck on the show is what
	 *  makes its transit a drive, whatever transport_mode says. */
	vehicles?: unknown[];
}

interface TimelineTour {
	id: string;
	name: string;
	transport_arranged?: boolean;
	events: TimelineEvent[];
	vehicles: TourVehicle[];
	vehicle_drone_capacity: number;
	vehicle_crew_capacity: number;
	max_event_crew: number;
	committed_total: number;
	window_start: string | null;
	window_end: string | null;
}

/* Accent per tour — imported, not re-derived. This page and the Calendar are
   documented to give a tour the SAME color, and that used to be guaranteed by two
   copies of one hash function sitting in two files. It is now one module
   (Events/event-accent.ts), so a palette change cannot desync them. Colours
   come from accentForSeed(tour.id, false), which returns oklch() strings: do not
   pass them to anything that parses hsl or hex. */

/* Ship dates more than this far from the show are treated as typos (same
   guard the Calendar uses) and the wing is dropped rather than drawn across
   the whole window. */
const SHIP_DATE_MAX_DAYS = 60;

const ymd = (d: dayjs.Dayjs) => d.format("YYYY-MM-DD");

/** Extract 'YYYY-MM-DD' from an API date that may be a plain string or ISO. */
const day10 = (v: string | null): string | null =>
	v ? String(v).slice(0, 10) : null;

const dayDiff = (a: string, b: string): number =>
	dayjs.utc(a).diff(dayjs.utc(b), "day");

interface Segment {
	startIdx: number; // inclusive day index
	endIdx: number; // inclusive day index
	kind: "bar" | "wing";
	label?: string;
	title?: string;
	clampedStart?: boolean; // real start preceded the window — bleed off the left
	clampedEnd?: boolean; // real end ran past the window — bleed off the right
}

/** Build bar + wing segments for a set of events, clamped to the window. */
function buildSegments(
	events: TimelineEvent[],
	dayIdx: Map<string, number>,
	windowStart: string,
	windowEnd: string,
	numDays: number,
	// A truck is assigned (to the tour, or to the standalone show). Transit is a
	// drive whenever one is, whatever transport_mode says: a show can carry a
	// truck AND shipped freight, and the mode stays "shipping" there.
	hauling: boolean,
): Segment[] {
	const segments: Segment[] = [];
	const clampIdx = (d: string): number => {
		if (d < windowStart) return 0;
		if (d > windowEnd) return numDays - 1;
		return dayIdx.get(d) ?? 0;
	};
	const inWindow = (start: string, end: string) =>
		start <= windowEnd && end >= windowStart;

	for (const e of events) {
		const start = day10(e.event_date);
		if (!start) continue;
		const end = day10(e.end_date) ?? start;
		const drones = (e.ls_drone_count || 0) + (e.pyro_drone_count || 0);
		const isDriving = hauling || e.transport_mode === "driving";

		if (inWindow(start, end)) {
			segments.push({
				startIdx: clampIdx(start),
				endIdx: clampIdx(end),
				kind: "bar",
				clampedStart: start < windowStart,
				clampedEnd: end > windowEnd,
				label: e.name,
				title: `${e.name} — ${formatEventDate(start)} · ${drones} drones`,
			});
		}

		const shipOut = day10(e.ship_out_date);
		if (
			shipOut &&
			shipOut < start &&
			dayDiff(start, shipOut) <= SHIP_DATE_MAX_DAYS &&
			inWindow(shipOut, start)
		) {
			segments.push({
				startIdx: clampIdx(shipOut),
				endIdx: clampIdx(start),
				kind: "wing",
				clampedStart: shipOut < windowStart,
				clampedEnd: start > windowEnd,
				title: `${e.name} — ${isDriving ? "drive out" : "ship out"} ${formatEventDate(shipOut)}`,
			});
		}

		const shipBack = day10(e.ship_back_date);
		if (
			shipBack &&
			shipBack > end &&
			dayDiff(shipBack, end) <= SHIP_DATE_MAX_DAYS &&
			inWindow(end, shipBack)
		) {
			segments.push({
				startIdx: clampIdx(end),
				endIdx: clampIdx(shipBack),
				kind: "wing",
				clampedStart: end < windowStart,
				clampedEnd: shipBack > windowEnd,
				title: `${e.name} — ${isDriving ? "drive back" : "ship back"} ${formatEventDate(shipBack)}`,
			});
		}
	}
	return segments;
}

/** Greedy lane packing: fit non-overlapping spans onto as few lanes as possible. */
function packLanes<T extends { startIdx: number; endIdx: number }>(
	items: T[],
): T[][] {
	const lanes: T[][] = [];
	const sorted = [...items].sort((a, b) => a.startIdx - b.startIdx);
	for (const item of sorted) {
		const lane = lanes.find(
			(l) => l[l.length - 1].endIdx + 1 < item.startIdx,
		);
		if (lane) lane.push(item);
		else lanes.push([item]);
	}
	return lanes;
}

const TourTimelinePage: React.FC = () => {
	const api = useApi();
	const toast = useToast();
	const user = useUserStore((state: any) => state.user);
	const canAssign = !!(
		user?.isadmin ||
		user?.can_admin ||
		user?.can_producer ||
		user?.can_operations
	);

	const [tours, setTours] = useState<TimelineTour[]>([]);
	const [events, setEvents] = useState<TimelineEvent[]>([]);
	const [loading, setLoading] = useState(true);
	const [offset, setOffset] = useState(0); // months from current month
	const [assigningTour, setAssigningTour] = useState<TimelineTour | null>(
		null,
	);
	const scrollRef = useRef<HTMLDivElement>(null);
	const didInitialScroll = useRef(false);

	// Window start: today on the default view (so the timeline opens on the
	// present, not stale past days); the first of the month once the user pages,
	// so ◀ ▶ land on clean months. Window end: a hard WINDOW_DAYS span from the
	// start — horizontal scroll covers it, and items past the edge peek in.
	const windowStart = useMemo(
		() =>
			offset === 0
				? ymd(dayjs.utc())
				: ymd(dayjs.utc().startOf("month").add(offset, "month")),
		[offset],
	);
	const windowEnd = useMemo(
		() => ymd(dayjs.utc(windowStart).add(WINDOW_DAYS, "day")),
		[windowStart],
	);
	const days = useMemo(
		() => enumerateDays(windowStart, windowEnd),
		[windowStart, windowEnd],
	);
	const dayIdx = useMemo(() => {
		const m = new Map<string, number>();
		days.forEach((d, i) => m.set(d, i));
		return m;
	}, [days]);
	const todayIdx = dayIdx.get(ymd(dayjs.utc()));

	const load = useCallback(async () => {
		try {
			const [tourRows, eventRows] = await Promise.all([
				api.get("/tours"),
				api.get("/events"),
			]);
			setTours(tourRows);
			setEvents(eventRows);
		} catch {
			toast.error("Failed to load timeline");
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	// On first data load, scroll today into view (offset 0 only).
	useEffect(() => {
		if (loading || didInitialScroll.current) return;
		didInitialScroll.current = true;
		if (todayIdx !== undefined && scrollRef.current) {
			scrollRef.current.scrollLeft = Math.max(
				0,
				todayIdx * DAY_W - scrollRef.current.clientWidth / 3,
			);
		}
	}, [loading, todayIdx]);

	// Tours intersecting the window (by their event/ship span)
	const visibleTours = useMemo(() => {
		return tours
			.map((tour) => {
				const segments = buildSegments(
					tour.events.filter((e) => e.status !== "cancelled"),
					dayIdx,
					windowStart,
					windowEnd,
					days.length,
					tour.vehicles.length > 0,
				);
				return { tour, segments };
			})
			.filter(({ segments }) => segments.length > 0)
			.sort((a, b) => {
				const aStart = Math.min(...a.segments.map((s) => s.startIdx));
				const bStart = Math.min(...b.segments.map((s) => s.startIdx));
				return aStart - bStart;
			});
	}, [tours, dayIdx, windowStart, windowEnd, days.length]);

	// Standalone events → packed faint lanes
	const standaloneLanes = useMemo(() => {
		const standalone = events.filter(
			(e) => !e.tour_id && e.status !== "cancelled",
		);
		const spans = standalone
			.map((e) => {
				const segs = buildSegments(
					[e],
					dayIdx,
					windowStart,
					windowEnd,
					days.length,
					(e.vehicles?.length ?? 0) > 0,
				);
				if (segs.length === 0) return null;
				return {
					event: e,
					segments: segs,
					startIdx: Math.min(...segs.map((s) => s.startIdx)),
					endIdx: Math.max(...segs.map((s) => s.endIdx)),
				};
			})
			.filter(Boolean) as {
			event: TimelineEvent;
			segments: Segment[];
			startIdx: number;
			endIdx: number;
		}[];
		return packLanes(spans);
	}, [events, dayIdx, windowStart, windowEnd, days.length]);

	const rangeLabel = `${dayjs.utc(windowStart).format("MMM D")} – ${dayjs
		.utc(windowEnd)
		.format("MMM D, YYYY")}`;

	// Month header cells: contiguous runs of days per month
	const monthRuns = useMemo(() => {
		const runs: { label: string; count: number }[] = [];
		for (const d of days) {
			const label = dayjs.utc(d).format("MMM YYYY");
			const last = runs[runs.length - 1];
			if (last && last.label === label) last.count++;
			else runs.push({ label, count: 1 });
		}
		return runs;
	}, [days]);

	const totalWidth = days.length * DAY_W;

	return (
		<div className="p-4 space-y-4">
			<div className="flex flex-wrap justify-between items-center gap-3">
				<div>
					<h1 className="text-2xl font-bold text-primary">
						Tour Timeline
					</h1>
					<p className="text-xs text-on-surface-variant mt-0.5">
						Tours, transit windows, and vehicle capacity at a glance
					</p>
				</div>
				<div className="flex items-center gap-2">
					<Tooltip label="Earlier">
					<IconButton label="Earlier" onClick={() => setOffset((o) => o - 1)}>
						<IconChevronLeft />
					</IconButton>
					</Tooltip>
					<div className="text-sm text-on-surface min-w-[200px] text-center">
						{rangeLabel}
					</div>
					<Tooltip label="Later">
					<IconButton label="Later" onClick={() => setOffset((o) => o + 1)}>
						<IconChevronRight />
					</IconButton>
					</Tooltip>
					<Appear show={offset !== 0}>
						<button
							onClick={() => setOffset(0)}
							className={buttonClass("quiet", "md")}
						>
							Today
						</button>
					</Appear>
				</div>
			</div>

			{loading && <LogoSpinner label="Loading tours" />}

			{!loading && (
				<FadeIn>
				<div
					ref={scrollRef}
					className="overflow-x-auto border border-gray-700/60 rounded-lg bg-gray-900"
				>
					<div style={{ width: totalWidth + 260 }} className="min-w-full">
						{/* ── Month header ── */}
						<div className="flex sticky top-0 z-10">
							<div className="sticky left-0 z-20 w-[260px] shrink-0 bg-gray-850 border-b border-r border-gray-700/60" />
							<div
								className="relative h-6 bg-gray-900 border-b border-gray-700/60"
								style={{ width: totalWidth }}
							>
								{(() => {
									let acc = 0;
									return monthRuns.map((run) => {
										const left = acc * DAY_W;
										acc += run.count;
										return (
											<div
												key={run.label}
												className="absolute top-0 h-full flex items-center px-2 text-[11px] font-semibold text-on-surface-variant border-l border-gray-700/40"
												style={{
													left,
													width: run.count * DAY_W,
												}}
											>
												{run.label}
											</div>
										);
									});
								})()}
							</div>
						</div>

						{/* ── Day header ── */}
						<div className="flex">
							<div className="sticky left-0 z-20 w-[260px] shrink-0 bg-gray-850 border-b border-r border-gray-700/60 px-3 py-1 text-[11px] text-on-surface-variant font-medium flex items-end">
								Tours
							</div>
							<div
								className="relative h-7 border-b border-gray-700/60"
								style={{ width: totalWidth }}
							>
								{days.map((d, i) => {
									const dd = dayjs.utc(d);
									const dow = dd.day();
									const isWeekend = dow === 0 || dow === 6;
									const isToday = i === todayIdx;
									return (
										<div
											key={d}
											className={`absolute top-0 h-full flex flex-col items-center justify-center text-[9px] leading-tight ${
												isToday
													? "text-primary-ink font-bold bg-primary/10"
													: isWeekend
														? "text-on-surface-disabled bg-surface-container-low"
														: "text-on-surface-variant"
											}`}
											style={{
												left: i * DAY_W,
												width: DAY_W,
											}}
										>
											<span>{dd.format("dd").charAt(0)}</span>
											<span>{dd.date()}</span>
										</div>
									);
								})}
							</div>
						</div>

						{/* ── Tour rows ── */}
						{visibleTours.length === 0 && (
							<div className="flex">
								<div className="sticky left-0 w-[260px] shrink-0 bg-gray-850 border-r border-gray-700/60" />
								<div className="px-4 py-8 text-sm text-on-surface-variant">
									No tours in this window.
								</div>
							</div>
						)}
						{visibleTours.map(({ tour, segments }) => (
							<TourRow
								key={tour.id}
								tour={tour}
								segments={segments}
								days={days}
								todayIdx={todayIdx}
								canAssign={canAssign}
								onAssign={() => setAssigningTour(tour)}
							/>
						))}

						{/* ── Standalone events (de-emphasized) ── */}
						{standaloneLanes.length > 0 && (
							<>
								<div className="flex">
									<div className="sticky left-0 z-20 w-[260px] shrink-0 bg-gray-850 border-y border-r border-gray-700/60 px-3 py-1 text-[10px] text-on-surface-variant uppercase tracking-widest font-semibold">
										Standalone shows
									</div>
									<div
										className="border-y border-gray-700/40"
										style={{ width: totalWidth }}
									/>
								</div>
								{standaloneLanes.map((lane, li) => (
									<div className="flex" key={`lane-${li}`}>
										<div className="sticky left-0 z-20 w-[260px] shrink-0 bg-gray-850/80 border-r border-gray-700/60" />
										<div
											className="relative"
											style={{
												width: totalWidth,
												height: LANE_H,
											}}
										>
											<GridBackdrop
												days={days}
												todayIdx={todayIdx}
											/>
											{lane.flatMap((span) =>
												span.segments.map((s, si) => (
													<TimelineSegment
														key={`${span.event.id}-${si}`}
														segment={s}
														accent={null}
														dim
													/>
												)),
											)}
										</div>
									</div>
								))}
							</>
						)}
					</div>
				</div>
				</FadeIn>
			)}

			{assigningTour && (
				<VehicleAssignmentModal
					tourId={assigningTour.id}
					tourName={assigningTour.name}
					assigned={assigningTour.vehicles}
					onClose={() => setAssigningTour(null)}
					onSaved={(updated) => {
						setAssigningTour(null);
						// Patch the tour in place — the scroll container never
						// unmounts, so position is preserved.
						setTours((prev) =>
							prev.map((t) => (t.id === updated.id ? updated : t)),
						);
					}}
				/>
			)}
		</div>
	);
};

/** Weekend bands + today line behind a row. */
const GridBackdrop: React.FC<{ days: string[]; todayIdx?: number }> = ({
	days,
	todayIdx,
}) => (
	<>
		{days.map((d, i) => {
			const dow = dayjs.utc(d).day();
			if (dow !== 0 && dow !== 6) return null;
			return (
				<div
					key={d}
					className="absolute top-0 h-full bg-surface-container-low pointer-events-none"
					style={{ left: i * DAY_W, width: DAY_W }}
				/>
			);
		})}
		{todayIdx !== undefined && (
			<div
				className="absolute top-0 h-full w-px bg-primary/50 pointer-events-none"
				style={{ left: todayIdx * DAY_W + DAY_W / 2 }}
			/>
		)}
	</>
);

const TimelineSegment: React.FC<{
	segment: Segment;
	accent: EventAccent | null;
	dim?: boolean;
}> = ({ segment, accent, dim }) => {
	const left = segment.startIdx * DAY_W;
	const width = (segment.endIdx - segment.startIdx + 1) * DAY_W;

	if (segment.kind === "wing") {
		// Dashed transit connector, vertically centered
		return (
			<div
				className="absolute pointer-events-none"
				style={{
					left,
					width,
					top: "50%",
					borderTop: `2px dashed ${
						accent !== null
							? dim ? accent.laneRingDim : accent.border
							: `rgba(156,163,175,${dim ? 0.2 : 0.4})`
					}`,
				}}
				title={segment.title}
			/>
		);
	}

	// Flatten the corner on any edge that was clamped so the bar bleeds off the
	// window rather than looking like it genuinely ends there.
	const R = 4;
	return (
		<div
			className="absolute flex items-center px-1.5 overflow-hidden"
			style={{
				left,
				width: Math.max(width, DAY_W),
				top: 4,
				bottom: 4,
				borderTopLeftRadius: segment.clampedStart ? 0 : R,
				borderBottomLeftRadius: segment.clampedStart ? 0 : R,
				borderTopRightRadius: segment.clampedEnd ? 0 : R,
				borderBottomRightRadius: segment.clampedEnd ? 0 : R,
				background:
					accent !== null
						? (dim ? accent.laneBgDim : accent.laneBg)
						: `rgba(107,114,128,${dim ? 0.12 : 0.25})`,
				boxShadow: `inset 0 0 0 1px ${
					accent !== null
						? (dim ? accent.laneRingDim : accent.border)
						: `rgba(156,163,175,${dim ? 0.25 : 0.4})`
				}`,
			}}
			title={segment.title}
		>
			<span
				className="text-[10px] font-medium truncate"
				style={{
					color: accent !== null ? accent.text : `rgba(209,213,219,${dim ? 0.5 : 0.9})`,
					opacity: accent !== null && dim ? 0.6 : undefined,
				}}
			>
				{segment.label}
			</span>
		</div>
	);
};

const TourRow: React.FC<{
	tour: TimelineTour;
	segments: Segment[];
	days: string[];
	todayIdx?: number;
	canAssign: boolean;
	onAssign: () => void;
}> = ({ tour, segments, days, todayIdx, canAssign, onAssign }) => {
	const accent = accentForSeed(tour.id, false);
	const isDriving = tour.events.some((e) => e.transport_mode === "driving");
	// Capacity is a real question exactly when a truck is carrying the gear, which
	// is what an assigned vehicle says. transport_mode was only ever a proxy for
	// that, and only because assigning a vehicle used to force it to "driving";
	// a show can now carry a truck AND shipped freight, where the mode stays
	// "shipping" and this chip would have vanished on an overloaded truck.
	const hauling = tour.vehicles.length > 0;
	const usable = tour.vehicles.filter(
		(v) => !["ooc", "retired"].includes(v.operational_status),
	);
	const unusable = tour.vehicles.filter((v) =>
		["ooc", "retired"].includes(v.operational_status),
	);
	const droneShort =
		hauling && tour.committed_total > tour.vehicle_drone_capacity;
	const crewShort =
		hauling &&
		tour.max_event_crew > 0 &&
		tour.max_event_crew > tour.vehicle_crew_capacity;

	return (
		<div className="flex group">
			{/* Sticky info cell */}
			<div className="sticky left-0 z-20 w-[260px] shrink-0 bg-gray-850 border-b border-r border-gray-700/60 px-3 py-2 space-y-1.5">
				<div className="flex items-center gap-1.5 min-w-0">
					<span
						className="w-2 h-2 rounded-full shrink-0"
						style={{ background: accent ? accent.text : "var(--outline)" }}
					/>
					<Link
						to={`/events/tours/${tour.id}`}
						className="text-sm font-semibold text-white truncate hover:text-primary transition-colors"
					>
						{tour.name}
					</Link>
				</div>

				<div className="flex flex-wrap items-center gap-1">
					{/* Drone capacity chip — hard warning whenever a truck is hauling */}
					<Appear show={hauling}>
						<Tooltip label={`Biggest show needs ${tour.committed_total} drones (incl. buffer) · assigned vehicles haul ${tour.vehicle_drone_capacity}`}>
							<Tag size="sm" tone={droneShort ? "bad" : "good"} className="tabular-nums">
								🛸 {tour.committed_total}/{tour.vehicle_drone_capacity}
							</Tag>
						</Tooltip>
					</Appear>
					{/* Crew seats — soft info warning (some crew fly in) */}
					<Appear show={crewShort}>
						<Tooltip label={`Largest event crew is ${tour.max_event_crew} but assigned vehicles seat ${tour.vehicle_crew_capacity}, so some crew may fly in`}>
							<Tag size="sm" tone="warn" className="tabular-nums">
								⚠ {tour.max_event_crew} crew / {tour.vehicle_crew_capacity}{" "}
								seats
							</Tag>
						</Tooltip>
					</Appear>
					<Appear show={!!tour.transport_arranged}>
						<Tooltip label="Transport arranged for a show on this tour">
							<Tag size="sm" tone="info">transport</Tag>
						</Tooltip>
					</Appear>
				</div>

				<div className="flex flex-wrap items-center gap-1">
					{usable.map((v) => (
						<Tooltip key={v.id} label={`${v.name ?? v.display_code} · ${v.drone_capacity ?? 0} drones · ${v.crew_capacity ?? 0} seats`}>
							<Tag size="sm">{v.name ?? v.display_code}</Tag>
						</Tooltip>
					))}
					{unusable.map((v) => (
						<Tooltip key={v.id} label={`${v.name ?? v.display_code} is ${v.operational_status}, so it is not counted toward capacity`}>
							<Tag size="sm" tone="bad" className="line-through">{v.name ?? v.display_code}</Tag>
						</Tooltip>
					))}
					<Appear show={isDriving && tour.vehicles.length === 0} className="text-[10px] text-amber-400/80">
						no vehicles assigned
					</Appear>
					{canAssign && (
						<Tooltip label="Assign vehicles">
							<button
								onClick={onAssign}
								className="inline-flex px-1.5 py-0.5 rounded ring-1 ring-inset ring-gray-700 text-[10px] text-on-surface-variant hover:text-primary hover:ring-primary/50 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
							>
								+ vehicles
							</button>
						</Tooltip>
					)}
				</div>
			</div>

			{/* Timeline lane */}
			<div
				className="relative border-b border-gray-700/40"
				style={{ width: days.length * DAY_W, minHeight: 56 }}
			>
				<GridBackdrop days={days} todayIdx={todayIdx} />
				{segments.map((s, i) => (
					<TimelineSegment key={i} segment={s} accent={accent} />
				))}
			</div>
		</div>
	);
};

export default TourTimelinePage;
